// @safetyhub/report-composer — 작성 페이지 위젯 트리 파생 (ComposerWidget)
//
// deriveWriteForm이 만든 InputWidget[](변수 → 입력 바인딩 → dataDef 순서)에 데이터 소스 행
// 그리드 위젯을 덧붙여 ComposerWidget 트리를 만든다.
// 결정: 2026-07-08-composer-writeform-editing-structure,
//       2026-09-30-writeform-to-composer-widget-tree-consolidation(S2 — 어댑터 소멸).
//
// ⚠️ 2026-09-30(S2): 종전에 여기 있던 어댑터 `inputWidgetFromNode`(flat 작성폼 노드 →
//   `InputWidget`)는 **삭제됐다** — `deriveWriteForm`이 `InputWidget[]`를 직접 내기 때문이다.
//   동작은 중립이다: 그 어댑터가 전파하던 필드 집합이 파생이 실제로 채우는 12필드를 전부 덮고
//   있었다(세는 법은 writeForm.ts 헤더 — 숫자를 인용하기 전에 그 명령으로 다시 세라).
//   이 이름을 되살리지 말 것 — 되살리면 같은 값을 한 번 더
//   옮기는 층이 생기고, 그 층은 새 필드를 조용히 떨어뜨린다(그것이 옛 주석이 거짓이 된 경로다).
//   2026-10-02(S6): 그 flat 타입(`WriteFormNode`)·저장 필드(`ReportTemplate.writeForm`)도
//   삭제됐으므로 이제 되살릴 입력 타입 자체가 없다(선언은 `InputWidget`이 흡수).
//
// ⚠️ 이 단계는 **baseline** — 그룹 없이 root 직속 InputWidget으로 변환한다.
//   그룹 자동 유도(섹션화)는 후속: GroupNode에 제목 필드가 없어 "섹션 제목 출처"(heading 노드/
//   운영자 지정 등) 결정 + 실 SMS 템플릿 검증이 선결. writeFormLayout(저장된 그룹/순서)이
//   도입되면 이 baseline 위에 재배치(assemble)한다.

import type {
  CatalogIndex,
  ComposerWidget,
  DataDef,
  InputOption,
  InputWidget,
  ReportTemplate,
  TemplateVariable,
} from '@safetyhub/shared';
import { deriveWriteForm } from './writeForm';
import { deriveDataSourceRowsWidgets } from './dataSourceWidgets';
import { applyRegisteredWrites } from './registeredWidgetWrites';

/**
 * 작성 페이지 위젯 트리 파생 (baseline: 그룹 없이 flat root).
 * deriveWriteForm(변수/입력/dataDef 순서)이 낸 InputWidget[] 뒤에,
 * 편집형 list 데이터 소스(template.dataSources)마다 dataSourceRows 행 그리드 위젯을 덧붙인다.
 * dataSourceRows의 bindingKey(=def.key)가 바인딩/dataDef 파생 키와 충돌하면 skip(중복 위젯 방지).
 * catalog는 deriveWriteForm과 동일 규약(없으면 카탈로그 바인딩 text 폴백).
 */
export function deriveComposerWidgets(
  template: ReportTemplate,
  catalog?: CatalogIndex,
): ComposerWidget[] {
  const derived = deriveWriteForm(template, catalog);
  const excludeKeys = new Set(derived.map((w) => w.bindingKey));
  const dataSourceRows = deriveDataSourceRowsWidgets(template.dataSources, excludeKeys);
  return [...derived, ...dataSourceRows];
}

/**
 * optionsFrom rows 채널 해소 — resolveDynamicSelectOptions(옵션 생성)와
 * applyDynamicSelectSelection(선택 전파의 원천 행 조회)이 공유하는 단일 규칙.
 * 우선순위(resolveSortableRows와 동형): ① resolvedRows[catalogKey](host 주입)
 * ② values[catalogKey](서버 resolvePrefill의 result[catalogKey]=rows 주입 — values 채널)
 * ③ outerValues[catalogKey] — **본문(최상위) 값**. 반복(repeat) 행 안에서는 `values`가 행 객체라
 *    본문에 박제된 기준표 rows(`risk-level-criteria-rows` 등)에 닿지 못한다. 편집 로드처럼 host
 *    prefill이 그 키를 안 주면(현장 세트가 다른 방식 / prefill 실패 best-effort) 행 스코프 select가
 *    옵션 0으로 떨어졌다(2026-09-14 위험성평가 결과서 3단계 문서 편집 실측 — content엔 3행이
 *    박제돼 있었다). `writeFormatScope`·branch condition의 branchScope와 같은 폴백 채널이다.
 *    최상위 렌더(outerValues 미전달)에선 ②까지만 — 회귀 0.
 */
function resolveDynamicSelectRows(
  optionsFrom: { catalogKey: string },
  values?: Record<string, unknown>,
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  outerValues?: Record<string, unknown>,
): Record<string, unknown>[] | undefined {
  const injected = resolvedRows?.[optionsFrom.catalogKey];
  if (Array.isArray(injected)) return injected;
  const fromValues = values?.[optionsFrom.catalogKey];
  if (Array.isArray(fromValues)) return fromValues as Record<string, unknown>[];
  const fromOuter = outerValues?.[optionsFrom.catalogKey];
  return Array.isArray(fromOuter) ? (fromOuter as Record<string, unknown>[]) : undefined;
}

/**
 * select 옵션 동적 소스(optionsFrom) 해소 — 카탈로그 rows에서 옵션 목록을 생성한다(개정 4).
 *
 * rows 채널은 resolveDynamicSelectRows(resolvedRows 우선 → values → outerValues(본문) 폴백).
 * 행 매핑: value=row[valueField], label=row[labelField](빈 label은 value로 대체).
 * value가 비문자열/빈 값인 행은 제외(옵션 성립 불가). rows 미해소/유효 옵션 0이면 **undefined**
 * 반환 — 호출부가 정적 options로 폴백한다(external 위임 등 카탈로그 미해소 환경 무크래시).
 */
export function resolveDynamicSelectOptions(
  optionsFrom: { catalogKey: string; valueField: string; labelField: string } | undefined,
  values?: Record<string, unknown>,
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  outerValues?: Record<string, unknown>,
): InputOption[] | undefined {
  if (!optionsFrom) return undefined;
  const rows = resolveDynamicSelectRows(optionsFrom, values, resolvedRows, outerValues);
  if (!rows || rows.length === 0) return undefined;
  const options: InputOption[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== 'object') continue;
    const rawValue = (row as Record<string, unknown>)[optionsFrom.valueField];
    // 옵션 value는 문자열 규격(<option value>). number 값 소스(기준표 level 등)는 String으로
    // 옵션화 — 위젯 dataType==='number'면 렌더/전파 경로가 Number로 되돌려 기록(문자열 소스는 불변).
    let value: string;
    if (typeof rawValue === 'string' && rawValue !== '') value = rawValue;
    else if (typeof rawValue === 'number' && Number.isFinite(rawValue)) value = String(rawValue);
    else continue;
    const rawLabel = (row as Record<string, unknown>)[optionsFrom.labelField];
    const label =
      typeof rawLabel === 'string' && rawLabel !== '' ? rawLabel : value;
    options.push({ value, label });
  }
  return options.length > 0 ? options : undefined;
}

/**
 * 동적 옵션 select(optionsFrom — 변수 파생 포함) 선택 적용 — ① 자기 값 onChange(bindingKey, 선택값)
 * ② writes가 있으면 선택 옵션의 **원천 행(raw)** 을 소스로 applyRegisteredWrites 전파(개정 5 —
 * applyComboboxSelection과 동형: source=행 필드 경로, target=bindingKey, 배열/객체 값 그대로 기록.
 * 예: 평가기준 세트 select writes=[{source:'criteria', target:'ra-criteria-detail'}]).
 *
 * 원천 행 채널: 옵션을 만든 것과 같은 rows(resolveDynamicSelectRows — resolvedRows 우선 → values
 * 폴백)에서 row[valueField]===선택값인 첫 행을 재조회한다(옵션에 raw 별도 보존 불요 — 최소 침습).
 * 콤보의 stale 회수(텍스트 재타이핑)와 달리 select는 옵션 간 전환뿐이라 회수 없음 — 새 선택의
 * 전파가 이전 값을 덮는다. 빈 선택('')·rows 미해소·행 미발견은 자기 값만 기록(전파 skip —
 * 정적 options 폴백 환경 무크래시). writes 미지정이면 기존 select와 완전 동일(자기 값만).
 */
export function applyDynamicSelectSelection(
  node: Pick<InputWidget, 'bindingKey' | 'optionsFrom' | 'writes' | 'dataType'>,
  selectedValue: string,
  values: Record<string, unknown>,
  onChange: (bindingKey: string, value: unknown) => void,
  resolvedRows?: Record<string, Record<string, unknown>[]>,
  /** 본문(최상위) 값 — 행 스코프에서 원천 행 재조회의 폴백 채널(옵션 생성과 같은 rows를 봐야 한다). */
  outerValues?: Record<string, unknown>,
): void {
  // 자기 값: dataType==='number'면 Number로 되돌려 기록(옵션 value는 String 규격이라, number 소스는
  // 저장/전파/서버 재산출이 숫자로 동작해야 함). 빈 선택('')은 undefined. 그 외는 문자열 그대로.
  const selfValue =
    node.dataType === 'number'
      ? selectedValue === ''
        ? undefined
        : Number(selectedValue)
      : selectedValue;
  onChange(node.bindingKey, selfValue);
  if (!node.writes?.length || !node.optionsFrom || selectedValue === '') return;
  const { valueField } = node.optionsFrom;
  const rows = resolveDynamicSelectRows(node.optionsFrom, values, resolvedRows, outerValues);
  // 원천 행 재조회는 String 비교(옵션 value가 String 규격이라 number 소스도 매칭).
  const row = rows?.find(
    (r) => r !== null && typeof r === 'object' && String(r[valueField]) === selectedValue,
  );
  if (row) applyRegisteredWrites(node.writes, row, values, onChange);
}

/**
 * 변수 optionsFrom이 참조하는 카탈로그 키 목록(중복 제거) — host가 prefill 결과의 rows를
 * resolvedRows 채널에 합류시킬 때 사용(sortableSelect 후보 소스 주입과 동형 — 모달 스코프 도달).
 */
export function collectVariableOptionsSourceKeys(
  variables: TemplateVariable[] | undefined,
): string[] {
  return [
    ...new Set(
      (variables ?? [])
        .map((v) => v.optionsFrom?.catalogKey)
        .filter((k): k is string => !!k),
    ),
  ];
}

/**
 * 저작 트리의 변수 파생 입력 위젯에 변수 선언의 동적 옵션 축을 반영한다(개정 4).
 *
 * 트리 배치본(placed copy)은 배치 시점 스냅샷이라 이후 변수 선언에 optionsFrom을 지정해도
 * 반영되지 않는다(syncComposerWidgets는 트리 무변형 — placed copy drift). 렌더 진입 전에
 * bindingKey가 변수 key와 일치하는 입력 위젯에 optionsFrom + 정적 options(폴백)를 변수 선언
 * 기준으로 덮어 **변수 선언을 옵션 축의 단일 정본**으로 만든다.
 *
 * 순회: root/group children/formTable 고정 행 셀 위젯. modalTrigger children은 모달 스코프
 * (본문 bindingKey와 격리)라 제외. optionsFrom 없는 변수는 무변(기존 동작·참조 유지).
 */
export function applyVariableDynamicOptions(
  tree: ComposerWidget[],
  variables: TemplateVariable[] | undefined,
): ComposerWidget[] {
  const dynamicVars = new Map(
    (variables ?? []).filter((v) => v.optionsFrom).map((v) => [v.key, v]),
  );
  if (dynamicVars.size === 0) return tree;

  const patchInput = (w: InputWidget): InputWidget => {
    const v = dynamicVars.get(w.bindingKey);
    if (!v || !v.optionsFrom) return w;
    return {
      ...w,
      optionsFrom: v.optionsFrom,
      // 정적 options(폴백)도 변수 선언 기준으로 갱신 — 배치본의 stale options 잔존 방지.
      ...(v.options ? { options: v.options } : {}),
    };
  };

  let changed = false;
  const walk = (nodes: ComposerWidget[]): ComposerWidget[] => {
    let nodesChanged = false;
    const next = nodes.map((w): ComposerWidget => {
      if (w.kind === 'input') {
        const patched = patchInput(w);
        if (patched !== w) nodesChanged = true;
        return patched;
      }
      if (w.kind === 'group') {
        const children = walk(w.children);
        if (children === w.children) return w;
        nodesChanged = true;
        return { ...w, children };
      }
      if (w.kind === 'formTable') {
        let tableChanged = false;
        const rows = w.rows.map((row) => {
          if (row.type !== 'fixed') return row;
          let rowChanged = false;
          const cells = row.cells.map((cell) => {
            if (!cell.widget) return cell;
            const patched = patchInput(cell.widget);
            if (patched === cell.widget) return cell;
            rowChanged = true;
            return { ...cell, widget: patched };
          });
          if (!rowChanged) return row;
          tableChanged = true;
          return { ...row, cells };
        });
        if (!tableChanged) return w;
        nodesChanged = true;
        return { ...w, rows };
      }
      return w;
    });
    if (!nodesChanged) return nodes;
    changed = true;
    return next;
  };

  const result = walk(tree);
  return changed ? result : tree;
}

/**
 * 위젯 트리에 저작된 기본값(defaultValue) 수집 — bindingKey→값 flat 맵.
 *
 * 작성 페이지(web)·작성 탭 캔버스(admin)의 **초기 content 시드 정본**이다. 렌더 fallback
 * (`value ?? defaultValue`)은 금지 — 표시만 되고 content에 없어 저장 시 탈락한다(보이는 것과
 * 저장이 어긋남). 기본값→prefill→edits 합성 순서의 첫 층으로 쓴다.
 *
 * 순회 범위:
 *  - input: `defaultValue !== undefined`만 수집. 같은 bindingKey 중복은 **첫 등장 우선**
 *    (collectPlacedInputKeys 등 기존 수집 헬퍼 규약과 동형).
 *  - group: children 재귀.
 *  - formTable: **고정(fixed) 행 셀 위젯**(cell.widget=InputWidget) 포함 — 셀 위젯 값도 flat
 *    bindingKey로 읽고 쓰므로(renderCellWidget) 본문 위젯과 같은 시드 경로. repeat 행은 행
 *    데이터(dataSource) 소관이라 제외.
 *  - modalTrigger: children **제외** — 모달 children input의 bindingKey는 모달 스코프 전용이라
 *    (결정 2026-07-15-composer-custom-modal-widget, collectPlacedInputKeys 스킵과 동일 이유)
 *    flat 본문 시드에 섞으면 본문의 같은 키를 오염시키고, 모달 스코프는 본문
 *    `values[trigger.bindingKey]` 객체에서만 복원되므로 flat 시드는 모달에 보이지도 않는다.
 *    모달 children 기본값은 ModalTriggerButton이 모달 열림 시(저장 상태 없을 때) 이 헬퍼를
 *    `widget.children`으로 직접 호출해 **모달 스코프**에 시드한다(스코프 무관 순수 함수).
 *  - design: bindingKey 없음 — 제외.
 *
 * @param dataDefs 지정 시 `widget:'table'` input의 bindingKey가 def.key와 일치하면 defaultValue
 *   대신 `def.defaultRows` deep clone으로 시드(기존 web new-composed/admin 캔버스 초기값 규칙 동일).
 */
export function collectWidgetDefaultValues(
  tree: ComposerWidget[],
  dataDefs?: DataDef[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const visit = (w: InputWidget): void => {
    if (w.bindingKey in result) return; // 첫 등장 우선
    if (w.widget === 'table') {
      const def = dataDefs?.find((d) => d.key === w.bindingKey);
      if (def) {
        result[w.bindingKey] = structuredClone(def.defaultRows);
        return;
      }
    }
    if (w.defaultValue !== undefined) result[w.bindingKey] = w.defaultValue;
  };
  const walk = (nodes: ComposerWidget[]): void => {
    for (const w of nodes) {
      switch (w.kind) {
        case 'input':
          visit(w);
          break;
        case 'group':
          walk(w.children);
          break;
        // branch: 투명 컨테이너 — children bindingKey는 부모(본문) 스코프 키(group 동형)라 flat 시드
        // 대상. 어느 슬롯이 활성이든 default 시드는 안전하므로 전 슬롯 하강.
        case 'branch':
          for (const slot of w.branches) walk(slot.children);
          break;
        case 'formTable':
          for (const row of w.rows) {
            if (row.type !== 'fixed') continue;
            for (const cell of row.cells) {
              if (cell.widget) visit(cell.widget);
            }
          }
          break;
        // modalTrigger: 모달 스코프 격리 — 위 doc 참조(children 하강 금지).
        // repeat: 행 스코프 격리 — children bindingKey는 행 객체 내부 필드(행 상대키)라 flat 본문
        // 시드에 섞으면 안 된다(모달 스코프 제외와 동일 이유). 행 값 시드는 데이터소스 소관
        // (서버 seed + 행 추가 createBlankRow).
        case 'modalTrigger':
        case 'repeat':
        case 'separator':
        case 'heading':
        case 'spacer':
          break;
      }
    }
  };
  walk(tree);
  return result;
}
